import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

type OfficeAction = {
  label: string;
  path?: string;
  moduleCode?: string;
  enabled?: boolean;
};

type OfficeCard = {
  title: string;
  primaryAction?: OfficeAction;
  secondaryActions?: OfficeAction[];
};

function ArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14" />
      <path d="m13 6 6 6-6 6" />
    </svg>
  );
}


function isActionEnabled(action: OfficeAction, canRead: (moduleCode: string) => boolean): boolean {
  const allowedByModule = action.moduleCode ? canRead(action.moduleCode) : true;
  return (action.enabled ?? true) && allowedByModule && Boolean(action.path);
}

function visibleSecondaryActions(actions: OfficeAction[] | undefined, canRead: (moduleCode: string) => boolean): OfficeAction[] {
  return (actions ?? []).filter((action) => isActionEnabled(action, canRead));
}

export function OfficePage() {
  const { canRead } = useAuth();

  const cards: OfficeCard[] = [
    {
      title: 'Kvalitet & HACCP',
      secondaryActions: [{ label: 'Kvalitet', path: '/quality', moduleCode: 'quality' }],
    },
    {
      title: 'Spårbarhet',
      primaryAction: { label: 'Till spårbarhet', path: '/traceability', moduleCode: 'traceability' },
    },
    {
      title: 'Lagerhantering',
      secondaryActions: [
        { label: 'Lageröversikt', path: '/ledger', moduleCode: 'ledger' },
        { label: 'Platser', path: '/sites', moduleCode: 'ledger' },
        { label: 'Inventering', path: '/ledger', moduleCode: 'ledger' },
        { label: 'Kalkyler', path: '/office/contractcalc', moduleCode: 'office' },
      ],
    },
    {
      title: 'Inköp & försäljning',
      secondaryActions: [
        { label: 'Inköp', path: '/purchases', moduleCode: 'receiving' },
        { label: 'Kundorder', path: '/orders', moduleCode: 'orders' },
        { label: 'Fortnox', path: '/office/fortnox', moduleCode: 'office' },
      ],
    },
    {
      title: 'Sensortrafik',
      primaryAction: { label: 'Öppna sensortrafik', path: '/office/sensor-traffic', moduleCode: 'office' },
    },
    {
      title: 'Register',
      secondaryActions: [
        { label: 'Produkter', path: '/products', moduleCode: 'products' },
        { label: 'Kunder', path: '/customers', moduleCode: 'customers' },
        { label: 'Leverantörer', path: '/suppliers', moduleCode: 'suppliers' },
      ],
    },
  ];

  const visibleCards = cards.filter((card) => {
    const primaryVisible = card.primaryAction ? isActionEnabled(card.primaryAction, canRead) : false;
    const secondaryVisible = visibleSecondaryActions(card.secondaryActions, canRead).length > 0;
    return primaryVisible || secondaryVisible;
  });

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <h1 className="text-[26px] font-semibold tracking-[-0.03em] text-slate-950 md:text-[30px]">Kontoret</h1>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {visibleCards.map((card) => {
          const primaryEnabled = card.primaryAction ? isActionEnabled(card.primaryAction, canRead) : false;
          const secondary = visibleSecondaryActions(card.secondaryActions, canRead);

          return (
            <div
              key={card.title}
              className="group flex min-h-[120px] flex-col rounded-[28px] border border-slate-300 bg-white px-5 py-5 shadow-[0_10px_30px_rgba(15,23,42,0.05)] transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_rgba(15,23,42,0.08)]"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="text-[18px] font-semibold tracking-[-0.02em] text-slate-950 md:text-[20px]">{card.title}</div>
              </div>

              <div className="pt-4">
                {primaryEnabled && card.primaryAction?.path && (
                  <Link to={card.primaryAction.path} className="inline-flex items-center gap-2 text-base font-semibold text-primary hover:text-primary-hover">
                    <span>{card.primaryAction.label}</span>
                    <ArrowIcon />
                  </Link>
                )}

                {secondary.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-3">
                    {secondary.map((action) => (
                      <Link
                        key={action.label}
                        to={action.path!}
                        className="inline-flex items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm transition hover:border-slate-400 hover:bg-slate-50"
                      >
                        <span>{action.label}</span>
                        <ArrowIcon />
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
